
import React, { useState } from 'react';
import { Calculator, CheckCircle2, ChevronDown, Languages, Volume2, Mic, Check } from 'lucide-react';

// --- MATH SOLVER WIDGET ---
// Logic: Step-by-step unlocking mechanism
export const MathSolver: React.FC<{ problem: string, steps: string[], isDark: boolean }> = ({ problem, steps, isDark }) => {
    const [unlockedStep, setUnlockedStep] = useState(-1);

    const containerClass = isDark ? "bg-slate-900/50 border-white/10" : "bg-white/80 border-blue-100 shadow-blue-100";
    const titleClass = isDark ? "text-blue-200" : "text-blue-700";
    const displayClass = isDark ? "text-white bg-white/5 border-blue-500/50" : "text-blue-900 bg-blue-50 border-blue-200";

    return (
        <div className={`w-full max-w-lg mx-auto backdrop-blur-md rounded-3xl p-6 md:p-8 border shadow-xl transition-colors ${containerClass}`}>
             <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-500/20 p-2 rounded-xl text-blue-500">
                    <Calculator size={24} />
                </div>
                <h3 className={`text-sm font-bold uppercase tracking-widest ${titleClass}`}>Logic Lab</h3>
             </div>
             
             {/* The Problem */}
             <div className="text-center mb-8">
                 <div className={`text-4xl md:text-5xl font-black rounded-2xl py-6 border-b-4 font-orbitron ${displayClass}`}>
                     {problem}
                 </div>
                 <p className={`text-xs mt-2 font-bold uppercase tracking-wide animate-pulse ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Tap steps to solve</p>
             </div>

             {/* Interactive Steps */}
             <div className="space-y-3">
                 {steps.map((step, idx) => {
                     const isUnlocked = idx <= unlockedStep;
                     const isNext = idx === unlockedStep + 1;
                     
                     let btnClass = "";
                     if (isUnlocked) {
                         btnClass = isDark 
                            ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-100' 
                            : 'bg-emerald-100 border-emerald-300 text-emerald-800';
                     } else if (isNext) {
                         btnClass = isDark 
                            ? 'bg-blue-600 border-blue-400 text-white shadow-lg animate-pulse'
                            : 'bg-blue-500 border-blue-400 text-white shadow-lg animate-pulse';
                     } else {
                         btnClass = isDark 
                            ? 'bg-slate-800/50 border-slate-700 text-slate-500 opacity-60 cursor-not-allowed'
                            : 'bg-slate-100 border-slate-200 text-slate-400 opacity-60 cursor-not-allowed';
                     }

                     return (
                         <button
                            key={idx}
                            disabled={!isNext && !isUnlocked}
                            onClick={() => isNext && setUnlockedStep(idx)}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 flex items-center justify-between group ${isNext ? 'scale-[1.02] cursor-pointer' : ''} ${btnClass}`}
                         >
                            <span className="font-bold text-lg">
                                {isUnlocked || isNext ? step : '???'}
                            </span>
                            {isUnlocked && <CheckCircle2 size={20} className="text-emerald-500" />}
                            {isNext && <ChevronDown size={20} className="text-white group-hover:translate-y-1 transition-transform" />}
                         </button>
                     )
                 })}
             </div>
             
             {unlockedStep === steps.length - 1 && (
                 <div className="mt-6 text-center animate-in zoom-in duration-300">
                     <span className="inline-block px-4 py-1 bg-yellow-400 text-slate-900 font-black rounded-full uppercase text-sm shadow-lg shadow-yellow-400/50">
                        Solved!
                     </span>
                 </div>
             )}
        </div>
    );
};

// --- VOICE PRACTICE WIDGET ---
// Logic: Simulated recording state and scoring
export const VoicePractice: React.FC<{ phrase: string, translation: string, tip: string, isDark: boolean, onPlay: (t:string) => void }> = ({ phrase, translation, tip, isDark, onPlay }) => {
    const [recording, setRecording] = useState(false);
    const [score, setScore] = useState<number | null>(null);

    const handleRecord = () => {
        if (recording) return;
        setRecording(true);
        setScore(null);
        // Simulate "Listening"
        setTimeout(() => {
            setRecording(false);
            setScore(Math.floor(Math.random() * 20) + 80); // Mock score 80-100
        }, 2000);
    };

    const containerClass = isDark ? "bg-white/10 border-white/20" : "bg-white/90 border-pink-200 shadow-xl shadow-pink-100";
    const textClass = isDark ? "text-white" : "text-slate-800";
    const subTextClass = isDark ? "text-pink-200" : "text-pink-600";

    return (
        <div className="w-full max-w-md mx-auto relative">
             <div className={`backdrop-blur-xl rounded-[2.5rem] p-8 border text-center relative overflow-hidden transition-colors ${containerClass}`}>
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-pink-500 to-transparent opacity-50"></div>
                
                <div className="inline-flex items-center gap-2 text-pink-500 text-xs font-bold uppercase tracking-widest mb-6 bg-pink-500/10 px-3 py-1 rounded-full border border-pink-500/20">
                    <Languages size={14}/> Speak Aloud
                </div>

                <h3 className={`text-4xl font-black mb-2 drop-shadow-md ${textClass}`}>{phrase}</h3>
                <p className={`text-lg mb-8 opacity-80 ${subTextClass}`}>{translation}</p>

                <div className="flex justify-center gap-6 mb-8">
                     <button onClick={() => onPlay(phrase)} className={`w-14 h-14 rounded-full border flex items-center justify-center transition-colors ${isDark ? 'bg-slate-800 border-white/10 text-white hover:bg-slate-700' : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-white'}`}>
                        <Volume2 size={24} />
                     </button>
                     
                     <div className="relative">
                        {recording && (
                            <div className="absolute inset-0 rounded-full bg-pink-500 animate-ping opacity-50"></div>
                        )}
                        <button 
                          onClick={handleRecord}
                          className={`w-20 h-20 rounded-full flex items-center justify-center text-white shadow-2xl transition-all active:scale-95 relative z-10 ${recording ? 'bg-pink-600 scale-110' : 'bg-gradient-to-br from-pink-500 to-rose-600 hover:scale-105'}`}
                        >
                            <Mic size={32} className={recording ? 'animate-pulse' : ''} />
                        </button>
                     </div>
                </div>

                {score !== null && (
                    <div className="animate-in slide-in-from-bottom-2 fade-in duration-500 bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-3">
                         <div className="flex items-center justify-center gap-2 text-emerald-500 font-bold">
                             <Check size={18} />
                             <span>Match: {score}%</span>
                         </div>
                         <p className="text-[10px] text-emerald-500/70 uppercase tracking-wider mt-1">Great pronunciation!</p>
                    </div>
                )}
                
                {!recording && score === null && (
                     <p className={`text-sm italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>"{tip}"</p>
                )}
             </div>
        </div>
    );
};
